'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useMenu } from '@/context/MenuContext';
import { useMainNavigationItems } from '@/constants/navigation-items';
import { Backdrop } from './Backdrop';
import { BackButton } from './BackButton';
import { SubNavigator } from './SubNavigator';
import { MainNavigator } from './MainNavigator';
import { SidebarFooter } from './SidebarFooter';
import Link from 'next/link';
import { Logo } from '../../Logo';

interface DesktopSidebarProps {
  setDesktopMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  desktopMenuOpen?: boolean;
}

const sidebarVariants = {
  hidden: { x: '100%' },
  visible: { x: 0 },
  exit: { x: '100%' },
};

const linkVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.3,
    },
  }),
  exit: { opacity: 0, x: 50 },
};

interface NavItem {
  name: string;
  href: string;
  subItems?: NavItem[];
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = () => {
  const { setDesktopMenuOpen, desktopMenuOpen } = useMenu();
  const navigation = useMainNavigationItems();
  const pathname = usePathname();

  // State to bepalen of we in de hoofdview of subview zitten.
  // Als activeSidebarSubNav null is, tonen we de hoofdview.
  // Anders tonen we de subview voor dat item.
  const [activeSidebarSubNav, setActiveSidebarSubNav] = useState<NavItem | null>(null);

  return (
    <AnimatePresence>
      {desktopMenuOpen && (
        <>
          {/* Backdrop */}
          <Backdrop setDesktopMenuOpen={setDesktopMenuOpen} setActiveSidebarSubNav={setActiveSidebarSubNav} />

          {/* Sidebar Container */}
          <motion.div
            className='fixed font-fractul top-0 right-0 w-96 h-full overflow-hidden bg-dark-900 shadow-xl z-50'
            initial='hidden'
            animate='visible'
            exit='exit'
            variants={sidebarVariants}
            transition={{
              duration: 0.4,
              ease: [0.2, 0, 0.58, 1],
            }}
          >
            <div className='flex items-center justify-between mt-2 py-12 px-6'>
              <Link href='/' className='-m-1.5 p-1.5 mt-2.5 px-4'>
                <span className='sr-only'>Teelor</span>
                <Logo width="96" />
              </Link>
            </div>

            <nav className=' px-4 sm:px-6 flex flex-col h-full'>
              <div className='flex-1'>
                <AnimatePresence mode='wait'>
                  {activeSidebarSubNav ? (
                    // SUBVIEW
                    <motion.div key='subview' initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 50 }} transition={{ duration: 0.3 }}>
                      {/* Back Button */}
                      <BackButton setActiveSidebarSubNav={setActiveSidebarSubNav} />
                      <div className='space-y-4'>
                        {activeSidebarSubNav.subItems?.map((sub, i) => {
                          const isActive = pathname === sub.href;
                          return <SubNavigator key={sub.name} sub={sub} i={i} setDesktopMenuOpen={setDesktopMenuOpen} setActiveSidebarSubNav={setActiveSidebarSubNav} isActive={isActive} linkVariants={linkVariants} />;
                        })}
                      </div>
                    </motion.div>
                  ) : (
                    // MAIN VIEW
                    <motion.div key='mainview' initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 50 }} transition={{ duration: 0.3 }} className='space-y-4'>
                      {navigation.map((item, i) => {
                        const hasSub = item.subItems && item.subItems.length > 0;
                        const isActive = pathname === item.href || (hasSub && item.subItems!.some((sub) => pathname.startsWith(sub.href)));
                        return <MainNavigator key={item.name} item={item} i={i} setDesktopMenuOpen={setDesktopMenuOpen} setActiveSidebarSubNav={setActiveSidebarSubNav} isActive={isActive} linkVariants={linkVariants} hasSub={hasSub} />;
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <SidebarFooter />
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DesktopSidebar;

// 14.2.15
